/**
 * CrmLeadsView — the Leads tab: status filter pills + search + a scannable, urgency-sorted list.
 * Rows open CrmLeadDetailModal (view/edit + qualify/disqualify/log). "New lead" opens it in create
 * mode. Loads once via getLeads() and filters client-side (Rating is a formula; no server sort).
 */
import { useEffect, useRef, useState } from 'react'
import { getLeads, LEAD_STATUSES } from '../../services/leads.js'
import CrmLeadDetailModal from './CrmLeadDetailModal.jsx'
import CrmNewLeadWizard from './CrmNewLeadWizard.jsx'

// Status pills are multi-select; the active funnel (New/Contacted/Engaged) is on by default so
// disqualified + converted leads (lower need) stay hidden until toggled on.
const STATUS_PILLS = [...LEAD_STATUSES, 'Converted']   // New, Contacted, Engaged, Qualified, Disqualified, Converted
// Qualified is included by default — it's the most actionable status ("ready to convert"), not
// a lower-need one like Disqualified/Converted, so hiding it by default contradicted the pill's
// own purpose and buried action-ready leads from the main view.
const DEFAULT_SELECTED = ['New', 'Contacted', 'Engaged', 'Qualified']
const STATUS_COLOR = { New: '#3A6EA5', Contacted: '#2B7A9E', Engaged: '#1D9E75', Qualified: '#1f9d57', Disqualified: '#9aa0a6' }
// Display language only — the Airtable status value stays 'Disqualified'; the app's own verb
// is "close out" (see CrmLeadDetailModal's CloseOutBox), so the list shows the matching noun.
const STATUS_LABEL = { Disqualified: 'Closed' }

function ratingEmoji(rating) {
  const r = String(rating || '')
  if (r.includes('HOT')) return '🔥'
  if (r.includes('WARM')) return '🌡️'
  if (r.includes('COLD')) return '❄️'
  return '⌛'
}

// Urgency sort: most days since contact first; "no interaction" (undefined) is most urgent.
function urgencyKey(lead) {
  const d = lead.daysSinceContact
  return (d === undefined || d === null) ? Infinity : Number(d)
}

export default function CrmLeadsView() {
  const [leads, setLeads] = useState(null)   // null = loading
  const [selected, setSelected] = useState(() => new Set(DEFAULT_SELECTED))
  const [q, setQ] = useState('')
  const [openLead, setOpenLead] = useState(undefined)  // undefined = closed; obj = view/edit
  const [createOpen, setCreateOpen] = useState(false)
  const loadedRef = useRef(false)

  function load() {
    setLeads(null)
    getLeads().then(setLeads).catch(() => setLeads([]))
  }
  useEffect(() => { if (!loadedRef.current) { loadedRef.current = true; load() } }, [])

  function onSaved(saved) {
    // Merge the saved lead into the list (or prepend if new), then keep the detail open with fresh data.
    setLeads(list => {
      const arr = Array.isArray(list) ? list.slice() : []
      const i = arr.findIndex(l => l.id === saved.id)
      if (i >= 0) arr[i] = saved
      else arr.unshift(saved)
      return arr
    })
    setOpenLead(cur => (cur && cur.id === saved.id) ? saved : cur)
  }

  function toggle(pill) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(pill)) next.delete(pill); else next.add(pill)
      return next
    })
  }
  // A converted lead lives in the "Converted" bucket regardless of its underlying status.
  const bucket = l => (l.converted ? 'Converted' : l.status)

  const list = Array.isArray(leads) ? leads : []
  const filtered = list
    .filter(l => selected.has(bucket(l)))
    .filter(l => {
      if (!q.trim()) return true
      const hay = `${l.name} ${l.organization} ${l.email}`.toLowerCase()
      return q.toLowerCase().split(/\s+/).every(tok => hay.includes(tok))
    })
    .sort((a, b) => urgencyKey(b) - urgencyKey(a))

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <div style={S.pills}>
          {STATUS_PILLS.map(p => (
            <button key={p} style={pillStyle(selected.has(p))} onClick={() => toggle(p)}>{STATUS_LABEL[p] || p}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <input style={S.search} placeholder="Search leads…" value={q} onChange={e => setQ(e.target.value)} />
        <button style={S.newBtn} onClick={() => setCreateOpen(true)}>+ New lead</button>
      </div>

      <div style={S.count}>
        {leads === null ? 'Loading…' : `${filtered.length} lead${filtered.length === 1 ? '' : 's'}`}
      </div>

      <div style={S.list}>
        {leads === null ? null : filtered.length === 0 ? (
          <div style={S.empty}>{selected.size === 0 ? 'Select a status to show leads.' : 'No leads match the selected filters.'}</div>
        ) : filtered.map(l => (
          <button key={l.id} style={S.row} onClick={() => setOpenLead(l)}>
            <span style={S.rate} title={l.rating}>{ratingEmoji(l.rating)}</span>
            <span style={S.nameCol}>
              <span style={S.name}>{l.name || '(no name)'}</span>
              {l.organization && <span style={S.org}>{l.organization}</span>}
            </span>
            <span style={{ ...S.statusBadge, background: (STATUS_COLOR[l.status] || '#5a7884') + '1a', color: STATUS_COLOR[l.status] || '#5a7884' }}>
              {l.converted ? 'Converted' : (STATUS_LABEL[l.status] || l.status || '—')}
            </span>
            <span style={S.days}>
              {l.daysSinceContact === undefined || l.daysSinceContact === null ? 'no contact' : `${l.daysSinceContact}d`}
            </span>
          </button>
        ))}
      </div>

      {openLead !== undefined && (
        <CrmLeadDetailModal lead={openLead} onClose={() => setOpenLead(undefined)} onSaved={onSaved} />
      )}
      {createOpen && (
        <CrmNewLeadWizard
          onClose={() => setCreateOpen(false)}
          onCreated={lead => { onSaved(lead); setOpenLead(lead) }}
        />
      )}
    </div>
  )
}

function pillStyle(on) {
  return {
    fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: on ? 600 : 400,
    color: on ? '#fff' : 'var(--aj-text-mid)', background: on ? '#07272D' : 'var(--aj-bg-muted)',
    border: 'none', borderRadius: 20, padding: '5px 13px', cursor: 'pointer',
  }
}

const S = {
  wrap: { maxWidth: 980, margin: '0 auto', padding: '18px 20px 60px' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  pills: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  search: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '7px 11px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, background: 'var(--aj-bg-elevated)', width: 200 },
  newBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 20, padding: '7px 15px', cursor: 'pointer' },
  count: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-mid)', margin: '2px 2px 8px' },
  list: { display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', overflow: 'hidden' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: 'none', border: 'none', borderBottom: '0.5px solid var(--aj-border)', cursor: 'pointer', textAlign: 'left', width: '100%' },
  rate: { fontSize: 15, width: 20, flexShrink: 0, textAlign: 'center' },
  nameCol: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  name: { fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  org: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  statusBadge: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '3px 10px', flexShrink: 0 },
  days: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-mid)', width: 76, textAlign: 'right', flexShrink: 0 },
  empty: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '24px 14px', textAlign: 'center' },
}
